import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Typography } from 'antd';
import type { ModalFooterRender } from 'antd/lib/modal/interface';

import { invalidateSession } from '@/shared/api';
import { useAntModal } from '@/shared/lib/hooks';
import { useAppDispatch } from '@/shared/lib/store';

import { useDeleteUserAccountMutation } from '../../api/profile.api';

import cls from './delete-account.module.css';

export const DeleteAccount = memo(() => {
    const { t } = useTranslation('profile-management');

    const dispatch = useAppDispatch();

    const modal = useAntModal();

    const [deleteUserAccountMutation, { isSuccess, isLoading }] =
        useDeleteUserAccountMutation();

    const deleteUserAccountCallback = useCallback(() => {
        deleteUserAccountMutation();
    }, [deleteUserAccountMutation]);

    const modalFooterRender = useCallback(
        (
            _: unknown,
            { OkBtn, CancelBtn }: Parameters<ModalFooterRender>[1],
        ) => (
            <Flex justify="center">
                <CancelBtn />
                <OkBtn />
            </Flex>
        ),
        [],
    );

    const onOpenConfirmModal = useCallback(() => {
        modal?.confirm?.({
            title: t('deleteAccountTitle'),
            okText: t('yes', { ns: 'translation' }),
            cancelText: t('no', { ns: 'translation' }),
            onOk: deleteUserAccountCallback,
            okButtonProps: {
                size: 'large',
            },
            cancelButtonProps: {
                size: 'large',
            },
            centered: true,
            icon: <ExclamationCircleOutlined className={cls.icon} />,
            footer: modalFooterRender,
            className: cls.confirmModal,
        });
    }, [deleteUserAccountCallback, modal, t, modalFooterRender]);

    useEffect(() => {
        if (isSuccess) {
            dispatch(invalidateSession({ isVoluntaryLogout: true }));
        }
    }, [isSuccess, dispatch]);

    return (
        <Flex
            className={cls.root}
            vertical
        >
            <Typography.Title
                className={cls.title}
                level={5}
            >
                {t('deleteAccountTitle')}
            </Typography.Title>

            <Typography.Paragraph className={cls.paragraph}>
                {t('deleteAccountDescription')}
            </Typography.Paragraph>

            <Divider className={cls.divider} />

            <Button
                className={cls.button}
                loading={isLoading}
                onClick={onOpenConfirmModal}
            >
                {t('deleteAccountButton')}
            </Button>
        </Flex>
    );
});

DeleteAccount.displayName = 'DeleteAccount';
